<template>
  <ui-card>
    <ui-title class="has-text-white mb-4">
      {{ $t('dashboard_flaws.title') }}
      <div class="float-right">
                  <button class="button is-primary" @click="GoToFlaws()">{{ $t('dashboard_flaws.all_flaws_button') }}</button>
			</div>
    </ui-title>

    <ui-tabs v-model="activeTab" :tabs="tabs" />

    <ui-tab>
      <ui-table
        :key="tableKey"
        ref="dataTable"
        service="flaw"
        method="getFlaws"
        rowHoverEffect
        :headers="headers"
        :queryParams="getQueryParams"
        :row-tint="getRowTintPerTab"
        :activeTab="activeTab"
        @rowClick="handleRowClick"
      >
        <template #item.actions="{ item }">
          <span class="actions-mail-btn" @click.stop="sendMail(item)" :class="readonly && 'disabled'">
            <i class="fas fa-envelope"></i>
          </span>
        </template>
      </ui-table>
    </ui-tab>
	</ui-card>
  
</template>

<script>
import { mapGetters } from 'vuex';
import DashboardModalMail from './modal/mail';
import iconMixin from '@/mixins/iconMixin.js';
import FlawService from '@/services/flaw';
import { loadFlawTaxonomy, saveFlawTaxonomy } from '@/services/offlineFlawTaxonomy';

export default {
  mixins: [iconMixin],
  name: 'DashboardFlawsTable',

  components: { DashboardModalMail },

  props: {
    readonly: { type: Boolean, default: false },
    customerId: { type: String, default: '' }
  },

  data: () => ({
    activeTab: 'toHandle',
    showMailSendModal: false,
    mailItem: null,
    taxonomyCategories: [],
    taxonomyRevision: 0
  }),

  computed: {
    ...mapGetters('user', ['getActiveCustomer', 'getActiveBuilding']),

    tableKey() {
      return `${this.$i18n.locale}-${this.taxonomyCategories.length}-${this.taxonomyRevision}`;
    },

    categoryById() {
      return (this.taxonomyCategories || []).reduce((acc, category) => {
        if (category?.id) acc[String(category.id)] = category;
        return acc;
      }, {});
    },

    typeById() {
      return (this.taxonomyCategories || []).reduce((acc, category) => {
        (category?.relatedFlawTypes || []).forEach(type => {
          if (type?.id) acc[String(type.id)] = type;
        });
        return acc;
      }, {});
    },

    localeCodes() {
      const messageLocales = Object.keys(this.$i18n?.messages || {});
      return messageLocales.length ? messageLocales : ['sv', 'en', 'fi'];
    },

    messageKeysByText() {
      const map = {};

      this.localeCodes.forEach(locale => {
        this.collectMessageEntries(this.$i18n?.getLocaleMessage?.(locale) || {}, '', map);
      });

      return map;
    },

   headers() {
      return [
        {
          text: this.$t('dashboard_flaws.header.item'),
          value: 'categoryLabel',
          sortable: true,
          transformFromParentObject: item => this.renderCategory(item)
        },
        {
          text: this.$t('dashboard_flaws.header.flaw'),
          value: 'name',
          sortable: true,
          transformFromParentObject: item => this.renderFlaw(item)
        },
        { text: this.$t('dashboard_flaws.header.found'), value: 'created', sortable: true,
          transform: val => this.$utils.date.getFullDate(val) },
        { text: this.$t('dashboard_flaws.header.round'), value: 'roundPlanningName', sortable: true },
        { text: '', value: 'actions' }
      ];
   },

    tabs() {
      return [
        { key: 'toHandle', label: this.$t('dashboard_flaws.tab.toHandle'), icon: 'fa-sticky-note' },
        { key: 'late',     label: this.$t('dashboard_flaws.tab.late'),     icon: 'fa-clock' },
        { key: 'done',     label: this.$t('dashboard_flaws.tab.done'),     icon: 'fa-check' },
        { key: 'all',      label: this.$t('dashboard_flaws.tab.all'),      icon: 'fa-align-left' }
      ];
    },

    getQueryParams() {
      const filter = {};

      if (this.activeTab === 'toHandle') {
        filter.done = false;
      }

      if (this.activeTab === 'late') {
        filter.done = false;
        filter.stop = this.moment(new Date()).subtract(30, 'days').toISOString();
      }

      if (this.activeTab === 'done') {
        filter.done = true;
      }

      return {
        customerId: this.customerId,
        buildingId: this.getActiveBuilding.id,
        ...filter
      };
    },

    getRowTintPerTab() {
      switch (this.activeTab) {
        case 'toHandle':
        case 'late':
          return '245 224 224';
        case 'done':
          return '227 245 224';
        default:
          return '245 224 224';
      }
    }
  },

  watch: {
    async '$i18n.locale'() {
      await this.loadFlawData();
      this.$nextTick(() => {
        if (this.$refs.dataTable?.fetchData) this.$refs.dataTable.fetchData();
      });
    }
  },

  created() {
    this.loadFlawData();
  },

  methods: {
    async loadFlawData() {
      const language = this.$i18n?.locale || 'sv';
      const cached = loadFlawTaxonomy(language);
      let changed = false;

      if (cached?.categories?.length) {
        this.taxonomyCategories = cached.categories;
        changed = true;
      }

      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        if (changed) this.taxonomyRevision += 1;
        return;
      }

      try {
        const response = await FlawService.GetFlawCategoriesWithFlawTypes(language);
        const raw = response?.data?.data ?? response?.data ?? response;
        const categories = Array.isArray(raw) ? raw : (raw?.categories || []);

        if (Array.isArray(categories)) {
          this.taxonomyCategories = categories;
          saveFlawTaxonomy(language, categories);
          changed = true;
        }
      } catch (error) {
      }

      if (changed) this.taxonomyRevision += 1;
    },

    expandTranslationKeys(key, prefix = '') {
      const raw = String(key || '').trim();
      if (!raw) return [];
      if (raw.includes('.')) return [raw];
      return prefix ? [`${prefix}.${raw}`, raw] : [raw];
    },

    translateKeyIfPresent(key, prefix = '') {
      const candidates = this.expandTranslationKeys(key, prefix);

      for (const candidate of candidates) {
        const translated = this.$t(candidate);
        if (translated !== candidate) return translated;
      }

      return '';
    },

    normalizeText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    },

    collectMessageEntries(source, prefix, map) {
      Object.entries(source || {}).forEach(([key, value]) => {
        const nextKey = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'string') {
          const normalized = this.normalizeText(value);
          if (!normalized) return;
          if (!map[normalized]) map[normalized] = [];
          if (!map[normalized].includes(nextKey)) map[normalized].push(nextKey);
          return;
        }

        if (value && typeof value === 'object') {
          this.collectMessageEntries(value, nextKey, map);
        }
      });
    },

    getTranslationForLocale(locale, key, prefix = '') {
      const message = this.$i18n?.getLocaleMessage?.(locale) || {};

      for (const candidate of this.expandTranslationKeys(key, prefix)) {
        const translated = this.$utils?.dotProp?.(message, candidate, '') || '';
        if (translated) return translated;
      }

      return '';
    },

    hasTranslationForAnyLocale(key, rawText, prefix = '') {
      const normalizedText = this.normalizeText(rawText);
      if (!key || !normalizedText) return false;

      return this.localeCodes.some(locale =>
        this.normalizeText(this.getTranslationForLocale(locale, key, prefix)) === normalizedText
      );
    },

    getTaxonomyCategory(item) {
      const categoryId =
        item?.fireProtectionFlawCategoryId ||
        item?.flawCategory?.id ||
        item?.flawCategoryId ||
        item?.mapItem?.fireProtection?.flawCategoryId;

      if (!categoryId) return null;
      return this.categoryById[String(categoryId)] || null;
    },

    getTaxonomyType(item) {
      if (item?.flawTypeId && this.typeById[String(item.flawTypeId)]) {
        return this.typeById[String(item.flawTypeId)];
      }

      const category = this.getTaxonomyCategory(item);
      const name = item?.name;
      if (!category || !name) return null;

      const categoryOtherType = (category.relatedFlawTypes || []).find(type =>
        String(type?.translationKey || '').toLowerCase().endsWith('other')
      );

      if (
        categoryOtherType &&
        this.hasTranslationForAnyLocale(categoryOtherType.translationKey, name, 'flaw.type')
      ) {
        return categoryOtherType;
      }

      return (category.relatedFlawTypes || []).find(type => {
        if (!type?.translationKey) return false;
        return this.hasTranslationForAnyLocale(type.translationKey, name, 'flaw.type')
          || this.normalizeText(type.translationKeyTranslated) === this.normalizeText(name);
      }) || null;
    },

    findGenericTranslationKey(text, prefix = '') {
      const normalized = this.normalizeText(text);
      if (!normalized) return '';

      let keys = this.messageKeysByText[normalized] || [];
      if (prefix) {
        keys = keys.filter(key => key === prefix || key.startsWith(`${prefix}.`));
      }

      if (!keys.length) return '';
      if (keys.length === 1) return keys[0];

      const currentLocaleValues = [...new Set(
        keys
          .map(key => this.normalizeText(this.$t(key)))
          .filter(Boolean)
      )];

      return currentLocaleValues.length === 1 ? keys[0] : '';
    },

    translateTextByMatchingKeys(text, prefix = '') {
      const key = this.findGenericTranslationKey(text, prefix);
      if (!key) return '';

      const translated = this.$t(key);
      return translated !== key ? translated : '';
    },

    getInventorySource(item) {
      return item?.mapItem?.fireProtection || item?.fireProtection || {
        ...item,
        name: item?.mapItemName || item?.name || '',
        companyId: item?.companyId ?? item?.mapItem?.fireProtection?.companyId,
        inventoryType: item?.inventoryType || item?.mapItem?.fireProtection?.inventoryType,
        flawCategoryId: item?.flawCategoryId ?? item?.mapItem?.fireProtection?.flawCategoryId,
        flawCategory: item?.flawCategory || item?.mapItem?.fireProtection?.flawCategory
      };
    },

    renderCategory(item) {
      const taxonomyCategory = this.getTaxonomyCategory(item);
      if (taxonomyCategory?.translationKey) {
        const translated = this.translateKeyIfPresent(taxonomyCategory.translationKey, 'flaw.category');
        if (translated) return translated;

        if (taxonomyCategory.translationKeyTranslated) {
          return taxonomyCategory.translationKeyTranslated;
        }
      }

      const translatedByCategoryId = this.displayType({
        flawCategoryId:
          item?.fireProtectionFlawCategoryId ||
          item?.flawCategory?.id ||
          item?.flawCategoryId,
        inventoryType: item?.inventoryType || item?.mapItem?.fireProtection?.inventoryType
      });
      if (translatedByCategoryId) return translatedByCategoryId;

      const preferredLabel = this.preferCategoryLabel(this.getInventorySource(item));
      if (preferredLabel) return preferredLabel;

      let key = item?.flawCategory?.translationKey;
      if (item?.flawCategoryTranslationKey) {
        key = item.flawCategoryTranslationKey;
      }

      if (key) {
        const translated = this.translateKeyIfPresent(key, 'flaw.category');
        if (translated) return translated;

        return item?.flawCategory?.translationKeyTranslated || key;
      }

      return item?.mapItemName || '';
    },

    renderFlaw(item) {
      const taxonomyType = this.getTaxonomyType(item);
      const translationKey =
        item?.flawType?.translationKey ||
        item?.flawTypeTranslationKey ||
        taxonomyType?.translationKey ||
        this.findGenericTranslationKey(item?.name, 'flaw.type');

      if (translationKey.toLowerCase().endsWith('other') && item?.name) {
        const isGenericOtherLabel = this.hasTranslationForAnyLocale(
          translationKey,
          item.name,
          'flaw.type'
        );

        if (!isGenericOtherLabel) {
          return this.tDescription(item.name);
        }
      }

      const translated = this.translateKeyIfPresent(translationKey, 'flaw.type');
      if (translated) return translated;

      const translatedByText = this.translateTextByMatchingKeys(item?.name, 'flaw.type');
      if (translatedByText) return translatedByText;

      const translatedDescription = this.tDescription(item?.name);
      if (translatedDescription) return translatedDescription;

      return item?.name || '';
    },

    sendMail(item) {
      this.mailItem = { ...item };
      this.showMailSendModal = true;
    },

    handleRowClick(item) {
      this.$emit('rowClick', item);
    },

		fetchData() {
			this.$refs.dataTable.fetchData();
		},
		GoToFlaws() {
			const buildingId = this.getActiveBuilding.id;
			console.log('clicked', buildingId);

			this.$router.push({
				name: 'RoundHistoryView',
				params: { buildingId: buildingId }
			});
		}


	}
};
</script>

<style lang="scss">
.Dashboard-table-switch {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 20px;
  background-color: lighten(#363a41, 20%);
  border: 1px solid darken(#363a41, 1%);
  box-shadow: rgba(0, 0, 0, 0.2) 0px 2px 4px 0px inset;

  i {
    color: white;
  }

  span {
    font-size: 12px;
    letter-spacing: 0.05rem;
    margin-right: 5px;
  }
}
</style>

<style lang="scss" scoped>
@import "@/assets/colors.scss";

.actions-mail-btn {
  cursor: pointer;

  &.disabled {
    pointer-events: none;

		> i {
			color: $light-grey;
		}
	}
}
.float-right {
	float: right;
	margin: 2px;
}
</style>
